import { useNavigate } from "react-router-dom";
import { Database, FolderArchive, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import { badgeFor, fmtBytes } from "@/lib/flightSessions";

// COCKPIT.FINAL.9 — Flight Session cockpit (LECTURE SEULE). Consomme la projection unique
// cockpit.flightSession (deriveFlightSessionView) : corrélation SID stricte, archive EN ATTENTE ≠ erreur,
// ULog/Bundle UNIQUEMENT depuis downloads.*. N'ouvre aucun WS, n'appelle aucune API, aucun 2e recorder.
const Row = ({ label, value, testid, color }) => (
  <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
    <span className="text-[9px] uppercase tracking-wider text-white/45">{label}</span>
    <span data-testid={testid} className="font-mono text-[10px]" style={{ color: color || "#E5E5E5" }}>{value ?? "—"}</span>
  </div>
);

const Badge = ({ value, testid }) => {
  const b = badgeFor(value);
  return <span data-testid={testid} className="font-mono text-[10px]" style={{ color: b.c }}>{b.l}</span>;
};

const ARCHIVE_LABEL = { available: "DISPONIBLE", waiting_archive: "EN ATTENTE", error: "ERREUR", idle: "—" };
const ARCHIVE_COLOR = { available: "#22C55E", waiting_archive: "#F59E0B", error: "#EF4444", idle: "#A3A3A3" };

export default function OperatorFlightSessionPanel({ droneId, sessionId, fs }) {
  const navigate = useNavigate();
  const v = fs || {};
  const rec = v.recorder || {};
  const up = v.uploader || {};
  const arc = v.archive || {};
  const sid = v.sessionId || sessionId || null;
  const bState = v.archiveState || "idle";
  const available = v.archiveAvailable === true;

  const copySid = () => {
    if (!sid) return;
    try { navigator.clipboard?.writeText(sid); toast.success("SID copié"); } catch { /* noop */ }
  };
  const openSession = () => {
    if (available && sessionId) navigate(`/flight-sessions/${encodeURIComponent(droneId)}/${encodeURIComponent(sessionId)}`);
  };

  const dropsColor = rec.drops ? "#F59E0B" : "#E5E5E5";
  const ulogLabel = v.ulogAvailable ? "DISPONIBLE" : (v.hasLease ? "EN ATTENTE" : "—");
  const ulogColor = v.ulogAvailable ? "#22C55E" : (v.hasLease ? "#F59E0B" : "#A3A3A3");
  const bundleDlLabel = v.bundleAvailable ? "DISPONIBLE" : (v.hasLease ? "EN ATTENTE" : "—");
  const bundleDlColor = v.bundleAvailable ? "#22C55E" : (v.hasLease ? "#F59E0B" : "#A3A3A3");

  return (
    <div data-testid="operator-flight-session" className="bg-[#141414] border border-white/10 rounded-sm">
      <div className="px-3 py-2 flex items-center gap-2 border-b border-white/10">
        <Database size={12} className="text-[#A78BFA]" />
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">Flight session</span>
        <span data-testid="fs-archive-state" className="ml-auto font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border"
          style={{ color: ARCHIVE_COLOR[bState], borderColor: ARCHIVE_COLOR[bState] + "66" }}>
          Archive {ARCHIVE_LABEL[bState] || bState}
        </span>
      </div>
      <div className="p-3 space-y-2">
        {/* Identité session (lease autoritaire) */}
        <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
          <span className="text-[9px] uppercase tracking-wider text-white/45">Session</span>
          <span className="flex items-center gap-1.5 min-w-0">
            <span data-testid="fs-sid" className="font-mono text-[10px] text-[#E5E5E5] truncate">{sid || "—"}</span>
            {v.live && <span data-testid="fs-live" className="font-mono text-[8px] px-1 rounded-sm bg-[#22C55E]/15 text-[#22C55E]">LIVE</span>}
            {sid && (
              <button data-testid="fs-copy-sid" onClick={copySid} title="Copier le SID" className="text-white/40 hover:text-white shrink-0">
                <Copy size={11} />
              </button>
            )}
          </span>
        </div>

        {/* RECORDER / UPLOADER (live Agent, corrélé SID strictement) */}
        <div data-testid="fs-recorder" className="grid grid-cols-2 gap-x-3">
          <Row label="Recorder" value={rec.correlated ? (rec.status || "—") : (rec.mismatch ? "MISMATCH" : "—")} testid="fs-recorder-status" color={rec.mismatch ? "#EF4444" : "#3B82F6"} />
          <Row label="Uploader" value={up.correlated ? (up.state || "—") : (up.mismatch ? "MISMATCH" : "—")} testid="fs-uploader-state" color={up.mismatch ? "#EF4444" : "#F59E0B"} />
          <Row label="Événements" value={rec.correlated ? (rec.eventsWritten ?? 0) : "—"} testid="fs-events" />
          <Row label="Commandes" value={rec.correlated ? (rec.commandsWritten ?? 0) : "—"} testid="fs-commands" />
          <Row label="Drops ev/cmd" value={rec.correlated ? `${rec.droppedEvents ?? 0} / ${rec.droppedCommands ?? 0}` : "—"} testid="fs-drops" color={dropsColor} />
          <Row label="Progression" value={up.correlated ? `${fmtBytes(up.uploadedBytes)} / ${fmtBytes(up.totalBytes)}` : "—"} testid="fs-progress" />
        </div>

        {/* ARCHIVE BACKEND (GET exact) */}
        <div data-testid="fs-archive" className="grid grid-cols-2 gap-x-3 pt-1 border-t border-white/10">
          <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
            <span className="text-[9px] uppercase tracking-wider text-white/45">ULog</span>
            <span data-testid="fs-ulog" className="font-mono text-[10px]" style={{ color: ulogColor }}>{ulogLabel}</span>
          </div>
          <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
            <span className="text-[9px] uppercase tracking-wider text-white/45">Bundle dl</span>
            <span data-testid="fs-bundle-download" className="font-mono text-[10px]" style={{ color: bundleDlColor }}>{bundleDlLabel}</span>
          </div>
          <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
            <span className="text-[9px] uppercase tracking-wider text-white/45">Bundle</span>
            <Badge value={arc.bundleStatus} testid="fs-bundle" />
          </div>
          <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
            <span className="text-[9px] uppercase tracking-wider text-white/45">Upload</span>
            <Badge value={arc.uploadStatus} testid="fs-upload" />
          </div>
          <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
            <span className="text-[9px] uppercase tracking-wider text-white/45">Intégrité</span>
            <Badge value={arc.integrityStatus} testid="fs-integrity" />
          </div>
          <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
            <span className="text-[9px] uppercase tracking-wider text-white/45">Analyse</span>
            <Badge value={v.analysisStatus} testid="fs-analysis" />
          </div>
        </div>

        {v.analysisError && (
          <div data-testid="fs-analysis-error" className="font-mono text-[9px] text-[#EF4444] break-words">{v.analysisError}</div>
        )}

        {/* Ouvrir la page détail (ne duplique AUCUN téléchargement lourd) */}
        <button data-testid="fs-open-session" onClick={openSession} disabled={!available}
          className="w-full flex items-center justify-center gap-1.5 font-mono text-[9px] uppercase tracking-wider rounded-sm px-3 py-2 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderColor: available ? "#22C55E66" : "#ffffff22", color: available ? "#22C55E" : "#A3A3A3" }}>
          {available ? <><ExternalLink size={12} /> Ouvrir session</> : <><FolderArchive size={12} /> Archive en attente</>}
        </button>
      </div>
    </div>
  );
}
